<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="isVisible"
        class="vm--container"
        :class="containerClass"
        @click.self="handleBackdropClick"
      >
        <div
          class="vm--modal"
          :class="modalClass"
          :style="modalStyle"
          role="dialog"
          aria-modal="true"
        >
          <slot :params="params" />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { modalState } from './index.js';

export default {
  name: 'Modal',
  props: {
    name: {
      type: String,
      required: true,
    },
    width: {
      type: [String, Number],
      default: 600,
    },
    height: {
      type: [String, Number],
      default: 'auto',
    },
    maxWidth: {
      type: [String, Number],
      default: null,
    },
    maxHeight: {
      type: [String, Number],
      default: null,
    },
    classes: {
      type: [String, Array],
      default: '',
    },
    clickToClose: {
      type: Boolean,
      default: true,
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    adaptive: {
      type: Boolean,
      default: false,
    },
    reset: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['before-open', 'opened', 'before-close', 'closed'],
  computed: {
    modalData() {
      return modalState.modals[this.name] || { visible: false, params: {} };
    },
    isVisible() {
      return this.modalData.visible;
    },
    params() {
      return this.modalData.params || {};
    },
    containerClass() {
      return {
        scrollable: this.scrollable,
      };
    },
    modalClass() {
      if (Array.isArray(this.classes)) {
        return this.classes;
      }
      return this.classes ? this.classes.split(' ') : [];
    },
    modalStyle() {
      const style = {};

      if (this.width) {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
      }

      if (this.height && this.height !== 'auto') {
        style.height = typeof this.height === 'number' ? `${this.height}px` : this.height;
      }

      if (this.maxWidth) {
        style.maxWidth = typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth;
      }

      if (this.maxHeight) {
        style.maxHeight = typeof this.maxHeight === 'number' ? `${this.maxHeight}px` : this.maxHeight;
      }

      return style;
    },
  },
  watch: {
    isVisible: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.$emit('before-open', { params: this.params });
          this.$nextTick(() => {
            this.$emit('opened');
          });
        } else if (!newVal && oldVal) {
          this.$emit('before-close');
          this.$nextTick(() => {
            this.$emit('closed');
          });
        }
      },
      immediate: false,
    },
  },
  methods: {
    handleBackdropClick() {
      if (this.clickToClose) {
        this.$modal.hide(this.name);
      }
    },
  },
};
</script>

<style>
/* Vue-js-modal compatible styles */
.vm--container {
  position: fixed;
  box-sizing: border-box;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.vm--container.scrollable {
  overflow-y: auto;
  align-items: flex-start;
  padding: 30px 0;
}

.vm--modal {
  position: relative;
  box-sizing: border-box;
  background-color: white;
  border-radius: 3px;
  box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
  max-width: 100%;
}

/* Transition styles */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
